import { Box, Spacer, Text } from '@jfs/ui-new'
import { getErrorMessage, getLoadingState } from '@jfs/utils'
import { useRoute } from '@react-navigation/native'
import debounce from 'lodash/debounce'
import { FC, useEffect, useRef, useState } from 'react'
import { bulkRefundTabData } from '../../data/static/data'
import { staticStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { ActionType } from '../../util/util'
import { FileUpload } from './FileUpload'
import { RefundsList } from './RefundsList'
import { Tabs } from './Tabs'
import styles from './styles'

interface BulkRefundProps {
  tab: number
  uploadProgress?: number
}

const BulkRefund: FC<BulkRefundProps> = ({ tab = 0 }: BulkRefundProps) => {
  const {
    mid,
    downloadSampleBulkRefundFile,
    fetchBulkSummary,
    pageNumber,
    getBulkSummaryData,
    selectedDateInfo,
    updateSelectedDate,
    fileUploadProgress,
    setFileUploadProgress,
    resetPagination,
    fetchBulkRefundOnDateChange,
    downloadFileApi,
    setUploadStatus,
    setSize,
    setCount,
    toDate,
  } = useStores((store) => ({
    mid: store.user.selectedMid,
    downloadSampleBulkRefundFile: store.bulkRefund.downloadSampleBulkRefundFile,
    fetchBulkSummary: store.bulkRefund.fetchBulkSummary,
    pageNumber: store.bulkRefund.pagination.number,
    getBulkSummaryData: store.bulkRefund.getBulkSummaryData,
    selectedDateInfo: store.bulkRefund.selectedDateInfo,
    updateSelectedDate: store.bulkRefund.updateSelectedDateInfo,
    fileUploadProgress: store.settings.metaData.fileUploadProgress,
    setFileUploadProgress: store.settings.metaData.setFileUploadProgress,
    resetPagination: store.bulkRefund.resetPagination,
    fetchBulkRefundOnDateChange: store.fetchBulkRefundOnDateChange,
    downloadFileApi: store.bulkRefund.downloadFileApi,
    setUploadStatus: store?.bulkRefund?.setUploadStatus,
    setSize: store?.bulkRefund?.setSize,
    setCount: store?.bulkRefund?.setCount,
    toDate: store.bulkRefund.selectedDateInfo.toDate,
  }))

  const themedStyle = styles(0)

  const [tabIndex, setTabIndex] = useState(tab)
  const [fileName, setFileName] = useState('')
  const [file, setFile] = useState(null)
  const didMount = useRef(false)

  const [fieldSettingVisible, setFieldSettingVisible] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)

  const path = useRoute().name
  const [fetchBulkSummaryList, fetchBulkSummaryHandler] = useLazyApiData(fetchBulkSummary)

  const apis = [fetchBulkSummaryHandler]
  const loadingState = getLoadingState(apis)
  const errorState = getErrorMessage(apis)

  const resetState = () => {
    setUploadStatus('NOT_SELECTED')
    setFileName('')
    setFile(null)
    setSize(null)
    setFieldSettingVisible(false)
    setFilterVisible(false)
    setFileUploadProgress(0)
    setCount(null)
  }

  const onFileSelected = (name: string, size: number, selectedFile: any) => {
    setFileName(name)
    setSize(size)
    setFile(selectedFile)
    setUploadStatus('IN_PROGRESS')
  }

  useEffect(() => {
    if (tabIndex === 1 && mid !== '') {
      if (didMount.current) {
        fetchBulkRefundOnDateChange(fetchBulkSummaryList)
      } else {
        fetchBulkRefundOnDateChange(fetchBulkSummaryList)
        didMount.current = true
      }
    }
  }, [fetchBulkSummaryList, fetchBulkRefundOnDateChange, mid, pageNumber, tabIndex, toDate])

  const onPress = () => {}

  const onHandleCancelChangeFile = () => {
    resetState()
    setTabIndex(0)
  }

  const navToHistory = () => {
    resetState()
    setTabIndex(1)
  }

  const fetchDetailOfPath = () => {
    resetPagination()
  }

  const bulkSummaryData = getBulkSummaryData()

  const emptyState = bulkSummaryData.length > 0 && !loadingState

  const scrollViewStyle = () => {
    if (bulkSummaryData?.length === 0) {
      return themedStyle.emptyContainer
    }
    return themedStyle.tableContainer(loadingState)
  }
  const scrollStyle = scrollViewStyle()

  const handleDownloadSampFile = () => {
    downloadSampleBulkRefundFile()
  }

  const onFileDownloadClick = (actionType: ActionType, id: any) => {
    console.log(actionType)
    const clickedItem = bulkSummaryData.find((element: any) => element.id === id)
    if (clickedItem) {
      downloadFileApi(clickedItem?.id, clickedItem?.fileName)
    }
  }

  const onSearchTextChange = debounce((fileNameQuery: string) => {
    fetchBulkSummaryList(mid, fileNameQuery, pageNumber, 20)
  }, 50)

  const onRetry = () => {
    if (tabIndex === 1) {
      fetchBulkSummaryList(mid, '', pageNumber, 10)
    }
  }

  return (
    <Box {...themedStyle.containerWeb}>
      <Text testID={'pageTitle'} type={'h0Bold'}>
        {staticStrings.bulkRefundLabel}
      </Text>
      {/** Tab Bar */}
      <Tabs
        path={'bulkRefund'} //Should be bulkRefund as its used to map the pagination in ActionsMenu
        tabIndex={tabIndex}
        bulkRefundTabData={bulkRefundTabData}
        setTabIndex={setTabIndex}
        setFieldSettingVisible={setFieldSettingVisible}
        onSearchTextChange={onSearchTextChange}
        setFilterVisible={setFilterVisible}
        fetchDetailOfPath={fetchDetailOfPath}
        selectedDateInfo={selectedDateInfo}
        updateSelectedDate={updateSelectedDate}
      />
      <Spacer size={32} />
      {/** File Upload */}
      {tabIndex === 0 && (
        <FileUpload
          progress={fileUploadProgress}
          navToHistory={navToHistory}
          name={fileName}
          onHandleCancelChangeFile={onHandleCancelChangeFile}
          mid={mid}
          tabIndex={tabIndex}
          onFileSelected={onFileSelected}
          handleDownloadSampFile={handleDownloadSampFile}
          file={file}
          resetState={resetState}
        />
      )}
      {/** History (List) */}
      {tabIndex === 1 && (
        <RefundsList
          path={path}
          fieldSettingVisible={fieldSettingVisible}
          onFileDownloadClick={onFileDownloadClick}
          filterVisible={filterVisible}
          onRetry={onRetry}
          loadingState={loadingState}
          errorState={errorState}
          emptyState={emptyState}
          scrollStyle={scrollStyle}
          listData={bulkSummaryData}
          onPress={onPress}
        />
      )}
    </Box>
  )
}

export default BulkRefund
